import React, { useState, useEffect } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Edit, Trash2, Plus, Building2, User, Archive, FolderArchive, AlertTriangle, RefreshCw, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Pagination, PaginationContent, PaginationItem } from "../components/ui/pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { toSydneyDate } from "../utils/helpers";

export default function DepartmentsPage() {
  const { departments, users, addDepartment, updateDepartment, deleteDepartment, assignDepartmentManager, archiveDepartment, unarchiveDepartment, procedures } = useData();
  const { currentUser } = useAuth();
  const [newDepartment, setNewDepartment] = useState({
    departmentCode: "",
    departmentName: "",
    departmentManager: ""
  });
  const [editingDepartment, setEditingDepartment] = useState<null | {
    id: string;
    departmentCode: string;
    departmentName: string;
    departmentManager: string;
  }>(null);
  const [openNew, setOpenNew] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openArchive, setOpenArchive] = useState(false);
  const [openUnarchive, setOpenUnarchive] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [archiveId, setArchiveId] = useState("");
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [archiveWarning, setArchiveWarning] = useState<null | { department: any; users: any[] }>(null);
  const [pendingArchiveDepartment, setPendingArchiveDepartment] = useState<any>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [openAssignManager, setOpenAssignManager] = useState(false);
  const [selectedManager, setSelectedManager] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Add search and pagination state
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Update handlers to reset page
  const handleShowArchived = (value: string) => {
    setShowArchived(value === "archived");
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const canEdit = currentUser?.userRole === "Admin" || currentUser?.userRole === "Director";

  // All users can be department managers, but non-manager roles will be promoted
  // Archived users are never selectable
  const eligibleRoles = ["Admin", "Director", "Manager"];
  const potentialManagers = users.filter(u => !u.archived);

  // Filter departments based on archived status
  const filteredDepartments = departments.filter(department => {
    const isArchived = department.archived === true;
    return showArchived ? isArchived : !isArchived;
  });

  // Add search filter
  const filteredDepartmentsWithSearch = filteredDepartments.filter(department => {
    if (searchTerm && !department.departmentName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Calculate pagination values
  const totalPages = Math.ceil(filteredDepartmentsWithSearch.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDepartments = filteredDepartmentsWithSearch.slice(startIndex, endIndex);

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [showArchived, searchTerm]);

  const handleAdd = async () => {
    if (!newDepartment.departmentCode || !newDepartment.departmentName) {
      toast.error("Department code and name are required");
      return;
    }

    setIsAdding(true);
    try {
      await addDepartment(newDepartment);
      setNewDepartment({
        departmentCode: "",
        departmentName: "",
        departmentManager: ""
      });
      setOpenNew(false);
      toast.success("Department added successfully");
    } catch (error) {
      console.error("Error adding department:", error);
      toast.error("Failed to add department");
    } finally {
      setIsAdding(false);
    }
  };

  const handleEdit = async () => {
    if (!editingDepartment || !editingDepartment.departmentName) {
      toast.error("Department name is required");
      return;
    }

    setIsUpdating(true);
    try {
      const updateData = {
        departmentName: editingDepartment.departmentName,
        departmentManager: editingDepartment.departmentManager
      };

      await updateDepartment(editingDepartment.id, updateData);

      setEditingDepartment(null);
      setOpenEdit(false);
      toast.success("Department updated successfully");
    } catch (error) {
      console.error("Error updating department:", error);
      toast.error("Failed to update department");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = () => {
    deleteDepartment(deleteId);
    setDeleteId("");
    setOpenDelete(false);
  };

  const handleArchive = () => {
    if (!currentUser) return;
    archiveDepartment(pendingArchiveDepartment.id, currentUser.id);
    setPendingArchiveDepartment(null);
    setOpenArchive(false);
  };

  const handleUnarchive = () => {
    if (!currentUser) return;
    unarchiveDepartment(archiveId, currentUser.id);
    setArchiveId("");
    setOpenUnarchive(false);
  };

  const getManager = (departmentManager: any) => {
    // If manager is already populated (object), return it directly
    if (typeof departmentManager === 'object' && departmentManager?.id) {
      return departmentManager;
    }
    // If manager is just an ID (string), look it up in users array
    if (typeof departmentManager === 'string') {
      return users.find(u => u.id === departmentManager);
    }
    return null;
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && openDelete && deleteConfirmation === 'delete') {
        handleDelete();
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [openDelete, deleteConfirmation, handleDelete]);

  // Add this helper function near the top with other helper functions
  const hasProcedureHistory = (departmentCode: string) => {
    return procedures.some(p => p.departmentId === departmentCode);
  };

  // Update the helper function to check both conditions
  const getDeleteRestrictions = (departmentCode: string) => {
    const hasUsers = users.some(u => u.department === departmentCode);
    const hasProcedures = procedures.some(p => p.departmentId === departmentCode);
    return { hasUsers, hasProcedures };
  };

  return (
    <TooltipProvider>
      <div className="p-2 md:p-0 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
          <h1 className="text-2xl font-bold">Departments</h1>
          {!showArchived && (
            <Can I={ACTIONS.CREATE} a={RESOURCES.DEPARTMENT}>
              <Dialog open={openNew} onOpenChange={setOpenNew}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2 w-full sm:w-auto"><Plus size={16} />Add Department</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Department</DialogTitle>
                    <DialogDescription>Create a new department with a code, name, and manager.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2"><Label htmlFor="departmentCode">Department Code *</Label><Input id="departmentCode" placeholder="e.g. 100" value={newDepartment.departmentCode} onChange={(e) => setNewDepartment({ ...newDepartment, departmentCode: e.target.value })} /></div>
                    <div className="space-y-2"><Label htmlFor="departmentName">Department Name *</Label><Input id="departmentName" placeholder="e.g. Administration" value={newDepartment.departmentName} onChange={(e) => setNewDepartment({ ...newDepartment, departmentName: e.target.value })} /></div>
                    <div className="space-y-2">
                      <Label htmlFor="departmentManager">Department Manager *</Label>
                      <p className="text-xs text-muted-foreground">Users with non-manager roles will be promoted.</p>
                      <Select value={newDepartment.departmentManager} onValueChange={(value) => setNewDepartment({ ...newDepartment, departmentManager: value })}>
                        <SelectTrigger><SelectValue placeholder="Select a manager" /></SelectTrigger>
                        <SelectContent>{potentialManagers.map((user) => <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                    <Button
                      onClick={handleAdd}
                      disabled={!newDepartment.departmentCode || !newDepartment.departmentName || !newDepartment.departmentManager || isAdding}
                    >
                      {isAdding ? "Adding Department..." : "Add Department"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Can>
          )}
        </div>

        {/* Tabs and Filters */}
        <div className="space-y-4">
          <Tabs defaultValue="active" onValueChange={handleShowArchived}>
            <TabsList className="grid w-full grid-cols-2 sm:w-fit"><TabsTrigger value="active">Active</TabsTrigger><TabsTrigger value="archived">Archived</TabsTrigger></TabsList>
          </Tabs>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search departments by name..." value={searchTerm} onChange={handleSearch} className="pl-10" />
          </div>
        </div>

        {/* Mobile View: Card List */}
        <div className="space-y-4 md:hidden">
          {currentDepartments.map((department) => {
            const manager = getManager(department.departmentManager);
            const { hasUsers, hasProcedures } = getDeleteRestrictions(department.departmentCode);
            const isDeleteDisabled = hasUsers || hasProcedures;

            // Construct the tooltip message
            let tooltipMessage = "Cannot delete: ";
            if (hasUsers && hasProcedures) {
              tooltipMessage += "Department has users and procedures.";
            } else if (hasUsers) {
              tooltipMessage += "Department has assigned users.";
            } else if (hasProcedures) {
              tooltipMessage += "Department has linked procedures.";
            }

            return (
              <Card key={department.id} className="overflow-hidden">
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <Link to={`/departments/${department.id}`} className="flex-1 min-w-0">
                      <h3 className="font-bold truncate flex items-center gap-2"><Building2 size={16} /> {department.departmentName}</h3>
                      <p className="text-sm text-muted-foreground mt-1">Code: {department.departmentCode}</p>
                    </Link>
                    <div className="flex items-center flex-shrink-0 gap-1">
                      {!showArchived ? (
                        <>
                          <Can I={ACTIONS.UPDATE} a={RESOURCES.DEPARTMENT}>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingDepartment({ id: department.id, departmentCode: department.departmentCode, departmentName: department.departmentName, departmentManager: manager?.id || "" }); setOpenEdit(true); }}><Edit size={18} /></Button>
                          </Can>
                          <Can I={ACTIONS.UPDATE} a={RESOURCES.ARCHIVE_DEPARTMENT}>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setPendingArchiveDepartment(department); setOpenArchive(true); }}><Archive size={18} /></Button>
                          </Can>

                          {/* MOBILE TOOLTIP WRAPPER */}
                          <Can I={ACTIONS.DELETE} a={RESOURCES.DEPARTMENT}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {/* The span is important for the tooltip to work on a disabled button */}
                                <span tabIndex={0}>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => { setDeleteId(department.id); setOpenDelete(true); }} disabled={isDeleteDisabled}>
                                    <Trash2 size={18} />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {isDeleteDisabled && (
                                <TooltipContent>
                                  <p>{tooltipMessage}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </Can>
                        </>
                      ) : (
                        <Can I={ACTIONS.UPDATE} a={RESOURCES.UNARCHIVE_DEPARTMENT}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setArchiveId(department.id); setOpenUnarchive(true); }}><RefreshCw size={14} /></Button>
                        </Can>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground pt-3 border-t">
                    <div className="flex items-center gap-2">
                      <User size={14} />
                      <strong>Manager:</strong>
                      {manager ? <Link to={`/people/${manager.id}`} className="text-primary hover:underline truncate">{manager.name}</Link> : "Not Assigned"}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Desktop View: Table */}
        <Card className="hidden md:block">
          <CardContent className="p-3">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Manager</TableHead>
                  {showArchived &&
                    <TableHead>Archived On</TableHead>}
                  {canEdit && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentDepartments.map((department) => {
                  const manager = getManager(department.departmentManager);
                  const { hasUsers, hasProcedures } = getDeleteRestrictions(department.departmentCode);
                  const isDeleteDisabled = hasUsers || hasProcedures;

                  // Construct the tooltip message
                  let tooltipMessage = "Cannot delete: ";
                  if (hasUsers && hasProcedures) {
                    tooltipMessage += "Department has users and procedures.";
                  } else if (hasUsers) {
                    tooltipMessage += "Department has assigned users.";
                  } else if (hasProcedures) {
                    tooltipMessage += "Department has linked procedures.";
                  }

                  return (
                    <TableRow key={department.id}>
                      <TableCell className="font-medium">{department.departmentCode}</TableCell>
                      <TableCell>
                        <Link to={`/departments/${department.id}`} className="hover:underline flex items-center gap-2">
                          <Building2 size={16} className="text-muted-foreground" />
                          {department.departmentName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {manager ? (
                          <div className="flex items-center gap-2">
                            <Link to={`/people/${manager.id}`} className="hover:underline flex items-center gap-2">
                              <User size={16} className="text-muted-foreground" />
                              {manager.name}
                            </Link>
                            <Badge variant="outline" className="ml-1 text-xs">
                              {manager.userRole}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {manager.jobRole}
                            </Badge>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      {showArchived && <TableCell>{toSydneyDate(department.archivedOn) || "N/A"}</TableCell>}
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {!showArchived ? (
                              <>
                                <Can I={ACTIONS.UPDATE} a={RESOURCES.DEPARTMENT}>
                                  <Button variant="outline" size="icon" onClick={() => { setEditingDepartment({ id: department.id, departmentCode: department.departmentCode, departmentName: department.departmentName, departmentManager: manager?.id || "" }); setOpenEdit(true); }}><Edit size={16} /></Button>
                                </Can>

                                <Can I={ACTIONS.UPDATE} a={RESOURCES.ARCHIVE_DEPARTMENT}>
                                  <Button variant="outline" size="icon" onClick={() => { setPendingArchiveDepartment(department); setOpenArchive(true); }}><Archive size={16} /></Button>
                                </Can>

                                {/* DESKTOP TOOLTIP WRAPPER */}
                                <Can I={ACTIONS.DELETE} a={RESOURCES.DEPARTMENT}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      {/* The span is important for the tooltip to work on a disabled button */}
                                      <span tabIndex={0}>
                                        <Button variant="destructive" size="icon" onClick={() => { setDeleteId(department.id); setOpenDelete(true); }} disabled={isDeleteDisabled}>
                                          <Trash2 size={16} />
                                        </Button>
                                      </span>
                                    </TooltipTrigger>
                                    {isDeleteDisabled && (
                                      <TooltipContent>
                                        <p>{tooltipMessage}</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </Can>
                              </>
                            ) : (
                              <Can I={ACTIONS.UPDATE} a={RESOURCES.UNARCHIVE_DEPARTMENT}>
                                <Button variant="outline" size="icon" onClick={() => { setArchiveId(department.id); setOpenUnarchive(true); }}><RefreshCw size={16} /></Button>
                              </Can>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {currentDepartments.length === 0 && (
          <Card><CardContent className="p-6 text-center text-muted-foreground">No departments found.</CardContent></Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem><Button variant="ghost" size="icon" onClick={() => handlePageChange(1)} disabled={currentPage === 1}><ChevronsLeft className="h-4 w-4" /></Button></PaginationItem>
              <PaginationItem><Button variant="ghost" size="icon" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}><ChevronLeft className="h-4 w-4" /></Button></PaginationItem>
              <PaginationItem><span className="p-2 text-sm font-medium">Page {currentPage} of {totalPages}</span></PaginationItem>
              <PaginationItem><Button variant="ghost" size="icon" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}><ChevronRight className="h-4 w-4" /></Button></PaginationItem>
              <PaginationItem><Button variant="ghost" size="icon" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}><ChevronsRight className="h-4 w-4" /></Button></PaginationItem>
            </PaginationContent>
          </Pagination>
        )}

        {/* All Dialogs (Edit, Delete, Archive, etc.) */}
        <Dialog open={openEdit} onOpenChange={setOpenEdit}>
          <DialogContent>
            {editingDepartment && (
              <>
                <DialogHeader><DialogTitle>Edit Department</DialogTitle></DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2"><Label>Code</Label><Input value={editingDepartment.departmentCode} disabled /></div>
                  <div className="space-y-2"><Label>Name</Label><Input value={editingDepartment.departmentName} onChange={(e) => setEditingDepartment({ ...editingDepartment, departmentName: e.target.value })} /></div>
                  <div className="space-y-2">
                    <Label>Manager</Label>
                    <Select value={editingDepartment.departmentManager} onValueChange={(value) => setEditingDepartment({ ...editingDepartment, departmentManager: value })}>
                      <SelectTrigger><SelectValue placeholder="Select a manager" /></SelectTrigger>
                      <SelectContent>{potentialManagers.map((user) => <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                  <Button onClick={handleEdit} disabled={isUpdating}>{isUpdating ? "Saving Changes..." : "Save Changes"}</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        <Dialog open={openDelete} onOpenChange={setOpenDelete}><DialogContent><DialogHeader><DialogTitle>Delete Department</DialogTitle><DialogDescription>Are you sure you want to delete this department? This action cannot be undone.</DialogDescription></DialogHeader><DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button variant="destructive" onClick={handleDelete}>Delete</Button></DialogFooter></DialogContent></Dialog>
        <Dialog open={openArchive} onOpenChange={setOpenArchive}><DialogContent><DialogHeader><DialogTitle>Archive Department</DialogTitle><DialogDescription>Are you sure you want to archive this department?</DialogDescription></DialogHeader><DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={handleArchive}>Archive</Button></DialogFooter></DialogContent></Dialog>
        <Dialog open={openUnarchive} onOpenChange={setOpenUnarchive}><DialogContent><DialogHeader><DialogTitle>Unarchive Department</DialogTitle><DialogDescription>This will make the department active again.</DialogDescription></DialogHeader><DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={handleUnarchive}>Unarchive</Button></DialogFooter></DialogContent></Dialog>
      </div>
    </TooltipProvider>
  );
}
